import {Box, Button, RingProgress, Text} from "@mantine/core";
import React from "react";
import {formatFileSize} from "@/common/utils/fileUtils";
import {IRawFile} from "../types";
import {IFileUploadStatus} from "../../protocols/types/protocol";
import classes from "./FileDisplayComponent.module.css"


export interface FileDisplayComponentProps {
    file?: IRawFile | IFileUploadStatus
    onDelete: (file: IRawFile) => void
}

const isRawFile = (file: IRawFile | IFileUploadStatus): file is IRawFile => {
    return ("subjectId" in file)
}

/**
 * FileDisplayComponent is a React functional component responsible for rendering
 * a visual representation of a file, including its name, size, and progress when
 * applicable. It supports different states based on the presence or type of the file.
 *
 * If the file is not provided, a default message prompting the user to
 * select a file will be displayed.
 *
 * For raw files, the component displays the file name and size with a delete button
 * to remove the file. For files with progress (e.g., during an upload), it renders a
 * progress indicator along with file details.
 *
 * Props:
 * - `onDelete` (Function): A callback function triggered when the delete button is clicked.
 * - `file` (IRawFile | IFileUploadStatus): A RawFile that has completed its upload or a
 *                                          raw file that is in progress of uploading.
 *
 * Returns:
 * A styled box element displaying the appropriate file information or message based on the
 * current state of the `file` prop.
 */
export const FileDisplayComponent: React.FC<FileDisplayComponentProps> = ({
                                                                              onDelete,
                                                                              file
                                                                          }) => {
    const renderFileDisplay = () => {
        if (!file) {
            return (
                <Text ta="center" size="xl" inline> Drop File here, or click to select. </Text>
            )
        } else if (isRawFile(file)) {
            return (
                <Box className={classes.fileContent}>
                    <Button style={{pointerEvents: "all"}}
                            size={"compact-xs"}
                            data-testid={`remove-file-${file.filename}`}
                            onClick={(ev) => {
                                ev.stopPropagation();
                                onDelete(file)
                            }}>
                        Delete
                    </Button>
                    <Text ta="center">{file.filename.replace(`${file.id}_`, "")}</Text>
                    <Text ta={"center"}>{file.size ? formatFileSize(file.size) : "unknown"}</Text>
                </Box>
            )
        } else {
            return (<Box className={classes.fileContent}>
                    <RingProgress size={60} rootColor={"transparent"}
                                  sections={[{value: file.progress * 100, color: "var(--bios-strong-accent)"}]}
                                  thickness={4}
                                  roundCaps={true}
                                  label={<Text ta={"center"}
                                               size={"0.75em"}>{`${Math.trunc(file.progress * 100).toString().padStart(3, ' ')}%`}</Text>}
                    />
                    <Box><Text ta="center">{file.file.filename.replace(`${file.file.id}_`, "")}</Text></Box>
                    <Box><Text ta="center">{formatFileSize(file.file.size || 0)}</Text></Box>
                </Box>
            )
        }
    }

    return (renderFileDisplay())
}
